"use client"

import { useId, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { Input, Label, Switch } from "@ui/shad"
import { toast } from "sonner"

import { useFindAndReplace } from "@/hooks/api/meetings/mutations"
import { SimpleFormDialog } from "@/components/dialogs"

interface FindAndReplaceDialogProps {
  meetingId: string
  transcriptHidden?: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onComplete?: () => void
}

export default function FindAndReplaceDialog({
  meetingId,
  transcriptHidden = false,
  open,
  onOpenChange,
  onComplete,
}: FindAndReplaceDialogProps) {
  const dialogFormId = useId()

  const [findText, setFindText] = useState("")
  const [replaceText, setReplaceText] = useState("")
  const [caseSensitive, setCaseSensitive] = useState(true)

  const findAndReplace = useFindAndReplace()
  const queryClient = useQueryClient()

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setFindText("")
      setReplaceText("")
      setCaseSensitive(true)
    }
    onOpenChange(newOpen)
  }

  async function handleSubmit() {
    if (!findText.trim()) return

    const data = await findAndReplace.mutateAsync({
      meetingId,
      data: {
        find: findText,
        replace_with: replaceText,
        case_sensitive: caseSensitive,
      },
    })

    if (data.replacement_count > 0) {
      toast.success(`Replaced ${data.replacement_count} occurrence${data.replacement_count === 1 ? "" : "s"}`)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["meeting"] }),
        queryClient.invalidateQueries({ queryKey: ["tasks"] }),
        queryClient.invalidateQueries({ queryKey: ["task"] }),
      ])
      onComplete?.()
    } else {
      toast.info("No matches found")
    }
    handleOpenChange(false)
  }

  return (
    <SimpleFormDialog
      dismiss="explicit"
      title={"Find and replace"}
      open={open}
      onOpenChange={handleOpenChange}
      action={{
        label: "Replace all",
        form: dialogFormId,
        disabled: !findText.trim() || findAndReplace.isPending,
        pending: findAndReplace.isPending,
      }}
      cancel={{ label: "Cancel", onClick: () => handleOpenChange(false), disabled: findAndReplace.isPending }}
    >
      <form
        id={dialogFormId}
        className="space-y-2"
        onSubmit={(event) => {
          event.preventDefault()
          if (!findText.trim() || findAndReplace.isPending) return
          void handleSubmit()
        }}
      >
        {
          <>
            <div className="space-y-4">
              <p>
                Fix AI transcription errors like misspelled names or incorrect terms. This replaces text in the AI
                summary, quick summary, tasks{transcriptHidden ? "" : ", and transcript"} all at once.
              </p>
              <div>
                <Label htmlFor="find-text" className="text-sm font-normal">
                  Find
                </Label>
                <Input
                  id="find-text"
                  placeholder="Text to find"
                  value={findText}
                  onChange={(e) => setFindText(e.target.value)}
                  disabled={findAndReplace.isPending}
                  className="mt-1"
                  autoFocus
                />
              </div>
              <div>
                <Label htmlFor="replace-text" className="text-sm font-normal">
                  Replace with
                </Label>
                <Input
                  id="replace-text"
                  placeholder="Replacement text (leave empty to delete)"
                  value={replaceText}
                  onChange={(e) => setReplaceText(e.target.value)}
                  disabled={findAndReplace.isPending}
                  className="mt-1"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch id="case-sensitive" checked={caseSensitive} onCheckedChange={setCaseSensitive} />
                <Label htmlFor="case-sensitive" className="text-sm font-normal">
                  Case sensitive
                </Label>
              </div>
            </div>
          </>
        }
      </form>
    </SimpleFormDialog>
  )
}
